import React, { useState } from "react";
import { get } from "../../services/api";
import jsPDF from "jspdf";
import "jspdf-autotable";

function DayWiseFeeCollection() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");

  // ---------- State ----------
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const formatDateInput = (date) => date.toISOString().split("T")[0];

  const [dateFrom, setDateFrom] = useState(formatDateInput(firstOfMonth));
  const [dateTo, setDateTo] = useState(formatDateInput(today));
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalReceived: 0,
    totalDiscount: 0,
    grandTotal: 0,
    totalRecords: 0,
  });

  // ---------- Helpers ----------
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    // Handle both ISO (YYYY-MM-DD) and API (DD-MM-YYYY) formats
    if (dateStr.includes("-")) {
      const parts = dateStr.split("-");
      if (parts[0].length === 4) {
        // YYYY-MM-DD to DD-MM-YYYY
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return dateStr;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "—";
    return Number(num).toLocaleString("en-PK", { minimumFractionDigits: 0 });
  };

  // ---------- Load report ----------
  const loadReport = async () => {
    if (!dateFrom || !dateTo) {
      alert("Please select both From and To dates.");
      return;
    }
    if (dateFrom > dateTo) {
      alert("Date From cannot be after Date To.");
      return;
    }

    setLoading(true);
    try {
      const response = await get(
        `StudentChallan/GetDailyRegistrationFeeCollection?CenterId=${centerId}&FromDate=${dateFrom}&ToDate=${dateTo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response && response.isSuccess) {
        setReportData(response.result?.details || []);
        setSummaryData(response.result?.summary || {
          totalReceived: 0,
          totalDiscount: 0,
          grandTotal: 0,
          totalRecords: 0,
        });
      } else {
        setReportData([]);
        setSummaryData({ totalReceived: 0, totalDiscount: 0, grandTotal: 0, totalRecords: 0 });
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Reset filters ----------
  const resetFilters = () => {
    const newFrom = formatDateInput(firstOfMonth);
    const newTo = formatDateInput(today);
    setDateFrom(newFrom);
    setDateTo(newTo);
    setReportData([]);
    setSummaryData({ totalReceived: 0, totalDiscount: 0, grandTotal: 0, totalRecords: 0 });
  };

  // ---------- Export to CSV ----------
  const exportToCSV = () => {
    if (reportData.length === 0) {
      alert("No data to export.");
      return;
    }

    const headers = ["Sr#", "Date", "Total", "Discount", "Received Amount"];
    const rows = reportData.map((r, index) => [
      index + 1,
      r.date,
      r.total,
      r.discount,
      r.receivedAmount,
    ]);

    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `DayWise_Fee_Collection_${dateFrom}_to_${dateTo}.csv`;
    link.click();
  };

  // ---------- Export to PDF ----------
  const exportToPDF = () => {
    if (reportData.length === 0) {
      alert("No data to export.");
      return;
    }
    const doc = new jsPDF("portrait");
    doc.text("Day Wise Fee Collection", 14, 15);
    doc.setFontSize(10);
    doc.text(`Date Range: ${formatDisplayDate(dateFrom)} – ${formatDisplayDate(dateTo)}`, 14, 22);

    const tableColumns = ["Sr#", "Date", "Total", "Discount", "Received Amount"];
    const tableRows = [];

    reportData.forEach((r, index) => {
      const rowData = [
        index + 1,
        r.date,
        formatNumber(r.total),
        formatNumber(r.discount),
        formatNumber(r.receivedAmount)
      ];
      tableRows.push(rowData);
    });

    // Add Grand Total
    const totalReceivedVal = reportData.reduce((sum, r) => sum + (r.receivedAmount || 0), 0);
    const totalDiscountVal = reportData.reduce((sum, r) => sum + (r.discount || 0), 0);
    const totalNetVal = reportData.reduce((sum, r) => sum + (r.total || 0), 0);
    
    tableRows.push([
      `Grand Total (${reportData.length} records)`, "",
      formatNumber(totalNetVal),
      formatNumber(totalDiscountVal),
      formatNumber(totalReceivedVal)
    ]);

    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 28,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246] },
      columnStyles: {
        0: { halign: 'center' },
        1: { halign: 'left' },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center' }
      },
      didParseCell: function (data) {
        // Enforce alignment based on column index
        const centeredCols = [0, 2, 3, 4];
        if (centeredCols.includes(data.column.index)) {
          data.cell.styles.halign = 'center';
        } else {
          data.cell.styles.halign = 'left';
        }

        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [17, 24, 39];
          if (data.column.index === 0) {
            data.cell.colSpan = 2; // Merge Sr# and Date
            data.cell.styles.halign = 'left'; // Keep "Grand Total" text left-aligned
          }
        }
      }
    });

    doc.save(`DayWise_Fee_Collection_${dateFrom}_to_${dateTo}.pdf`);
  };

  // ---------- Calculate totals ----------
  const totalReceivedCalc = reportData.reduce((sum, r) => sum + (r.receivedAmount || 0), 0);
  const totalDiscountCalc = reportData.reduce((sum, r) => sum + (r.discount || 0), 0);
  const totalNetCalc = reportData.reduce((sum, r) => sum + (r.total || 0), 0);

  // ---------- Styles (matching BillingReport) ----------
  const inputStyle = {
    height: "36px",
    padding: "0 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "13px",
    minWidth: "150px",
    outline: "none",
    color: "#111827",
    backgroundColor: "#ffffff",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#6b7280",
    marginBottom: "4px",
    display: "block",
  };

  const btnPrimary = {
    height: "36px",
    padding: "0 18px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#3b82f6",
    color: "#fff",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  };

  const btnSecondary = {
    height: "36px",
    padding: "0 18px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    color: "#374151",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  };

  const summaryCards = [
    { label: "Total Records", value: summaryData.totalRecords || reportData.length, color: "#3b82f6", bg: "#eff6ff" },
    { label: "Total Received", value: formatNumber(summaryData.totalReceived || totalReceivedCalc), color: "#10b981", bg: "#f0fdf4" },
    { label: "Total Discount", value: formatNumber(summaryData.totalDiscount || totalDiscountCalc), color: "#f59e0b", bg: "#fffbeb" },
    { label: "Grand Total", value: formatNumber(summaryData.grandTotal || totalNetCalc), color: "#8b5cf6", bg: "#f5f3ff" },
  ];

  return (
    <div style={{ paddingBottom: "20px", fontFamily: "Inter, Arial, sans-serif" }}>
      {/* Header with title and action buttons */}
      <div
        className="no-print"
        style={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
          padding: "20px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#111827" }}>Day Wise Fee Collection</h2>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#6b7280" }}>
            Daily fee collection summary by date range
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={exportToCSV}
            style={{ ...btnSecondary, display: "flex", alignItems: "center", gap: "6px" }}
          >
            📥 Export CSV
          </button>
          <button
            onClick={exportToPDF}
            style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: "6px" }}
          >
            📄 Export PDF
          </button>
        </div>
      </div>

      <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Filter Panel */}
        <div
          className="no-print"
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px 24px",
          }}
        >
          <p style={{ margin: "0 0 14px", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Filters</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
            {/* Date From */}
            <div>
              <label style={labelStyle}>Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={inputStyle}
              />
            </div>
            {/* Date To */}
            <div>
              <label style={labelStyle}>Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={inputStyle}
              />
            </div>
            {/* Buttons */}
            <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
              <button onClick={loadReport} style={btnPrimary}>
                Generate Report
              </button>
              <button onClick={resetFilters} style={btnSecondary}>
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {reportData.length > 0 && (
          <div className="no-print" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
            {summaryCards.map((card, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    backgroundColor: card.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: card.color,
                    }}
                  />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "#6b7280",
                    }}
                  >
                    {card.label}
                  </div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#111827",
                      marginTop: "2px",
                    }}
                  >
                    {card.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {/* Table header with record count */}
          <div
            style={{
              padding: "14px 20px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#f9fafb",
            }}
          >
            <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>Day Wise Fee Collection Details</span>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              {reportData.length} record{reportData.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  {["Sr#", "Date", "Total", "Discount", "Received Amount"].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        padding: "11px 18px",
                        textAlign: i === 1 ? "left" : "center",
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#6b7280",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>
                      <div style={{ fontSize: "28px", marginBottom: "8px" }}>⏳</div>
                      Loading data…
                    </td>
                  </tr>
                )}
                {!loading && reportData.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>
                      <div style={{ fontSize: "28px", marginBottom: "8px" }}>📋</div>
                      No records found for the selected date range.
                    </td>
                  </tr>
                )}
                {!loading &&
                  reportData.map((item, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #f3f4f6",
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#eff6ff")}
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#ffffff" : "#fafafa")
                      }
                    >
                      <td style={{ padding: "12px 18px", fontSize: "13px", color: "#6b7280", textAlign: "center" }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: "12px 18px", fontSize: "13px", color: "#3b82f6", fontFamily: "monospace", textAlign: "left" }}>
                        {item.date}
                      </td>
                      <td style={{ padding: "12px 18px", fontSize: "13px", textAlign: "center", color: "#111827" }}>
                        {formatNumber(item.total)}
                      </td>
                      <td
                        style={{
                          padding: "12px 18px",
                          fontSize: "13px",
                          textAlign: "center",
                          color: item.discount > 0 ? "#f59e0b" : "#9ca3af",
                          fontWeight: item.discount > 0 ? 500 : 400,
                        }}
                      >
                        {formatNumber(item.discount)}
                      </td>
                      <td
                        style={{
                          padding: "12px 18px",
                          fontSize: "13px",
                          textAlign: "center",
                          fontWeight: 700,
                          color: "#10b981",
                        }}
                      >
                        {formatNumber(item.receivedAmount)}
                      </td>
                    </tr>
                  ))}
              </tbody>
              {!loading && reportData.length > 0 && (
                <tfoot>
                  <tr style={{ backgroundColor: "#f0f9ff", borderTop: "2px solid #e5e7eb" }}>
                    <td colSpan="2" style={{ padding: "13px 18px", fontWeight: 700, fontSize: "13px", color: "#111827" }}>
                      Grand Total ({reportData.length} records)
                    </td>
                    <td style={{ padding: "13px 18px", textAlign: "center", fontWeight: 700, fontSize: "13px", color: "#111827" }}>
                      {formatNumber(totalNetCalc)}
                    </td>
                    <td style={{ padding: "13px 18px", textAlign: "center", fontWeight: 700, fontSize: "13px", color: "#f59e0b" }}>
                      {formatNumber(totalDiscountCalc)}
                    </td>
                    <td style={{ padding: "13px 18px", textAlign: "center", fontWeight: 700, fontSize: "14px", color: "#10b981" }}>
                      {formatNumber(totalReceivedCalc)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Generation timestamp */}
        {reportData.length > 0 && (
          <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", textAlign: "right" }}>
            Report generated on {new Date().toLocaleString()}
          </p>
        )}
      </div>

    </div>
  );
}

export default DayWiseFeeCollection;
